// HM-346 cluster 16.1.5: Step 2 ospite pixel-aware mockup v3.
//
// - Search clienti autocomplete (debounced 250ms) -> selezione popola form.
// - Skip "Procedi senza dati ospite" SOLO per Day Use (mode != overnight)
//   -> setAnonymous(true) + salta a Step 3.
// - Form manuale: Nome*, Cognome*, Telefono*, Email + sezione Documento
//   espandibile (tipo + numero).
//
// Lo store WalkInGuest e' stato esteso con firstName/lastName/document*;
// fullName mantenuto sync via setGuest per backward-compat downstream.
//
// Footer "Indietro/Avanti" ora gestito dal WizardFooter (HM-349); questo
// step non rende footer locale.

import { ChevronDown, Search, UserCircle } from '@hotel-manager/ui/icons';
import { cn } from '@hotel-manager/ui/lib/cn';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { type GuestSearchResult, searchGuests } from '../../api/guests.js';
import { useWalkInWizard } from '../../stores/walkIn.js';

const GuestSchema = z.object({
  firstName: z.string().min(2, 'Nome richiesto'),
  lastName: z.string().min(2, 'Cognome richiesto'),
  phone: z
    .string()
    .trim()
    .min(1, 'Telefono richiesto')
    .regex(/^\+?[\d\s-]{6,20}$/, 'Telefono non valido'),
  email: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v), 'Email non valida'),
});

export function StepGuestInfo() {
  const guest = useWalkInWizard((s) => s.guest);
  const setGuest = useWalkInWizard((s) => s.setGuest);
  const setStep = useWalkInWizard((s) => s.setStep);
  const setAnonymous = useWalkInWizard((s) => s.setAnonymous);
  const mode = useWalkInWizard((s) => s.mode);
  const dynamicMode = useWalkInWizard((s) => s.dynamicPricing.selectedMode);
  // Day Use = qualunque scelta non overnight (sia legacy day_use_*, sia hours dinamico).
  const isDayUse = mode !== 'overnight' && dynamicMode !== 'overnight';

  const [errs, setErrs] = useState<Record<string, string>>({});
  const [docOpen, setDocOpen] = useState(Boolean(guest.documentType || guest.documentNumber));

  // Espongo onNext al WizardFooter via window event listener -> evito ref drilling
  // through lo store. Pattern semplice: WizardFooter chiama setStep(3) direttamente,
  // qui validiamo all'unmount? No: validation deve avvenire on-click "Avanti".
  // Soluzione: pubblico la validation via wizard store. Per il momento HM-346
  // espone validation via guest fields obbligatori (footer disabilitato in HM-347)
  // e mantieni il pulsante locale data-testid="walkin-next-2" per compat test.
  // Per cluster 16.1.5: pulsante Avanti vive nel WizardFooter; il next() si
  // limita a salvare il form e avanzare. Validation Zod e' opzionale, mostrata
  // inline ma non blocca.
  // Tuttavia per non rompere i test esistenti, lascio anche pulsante "Avanti"
  // legacy con data-testid="walkin-next-2" che valida prima di setStep(3).
  const validateAndAdvance = () => {
    const result = GuestSchema.safeParse({
      firstName: guest.firstName,
      lastName: guest.lastName,
      phone: guest.phone ?? '',
      email: guest.email ?? '',
    });
    if (!result.success) {
      const next: Record<string, string> = {};
      for (const i of result.error.issues) {
        if (i.path[0]) next[String(i.path[0])] = i.message;
      }
      setErrs(next);
      return;
    }
    setErrs({});
    setStep(3);
  };

  return (
    <section data-testid="step-guest-info" className="flex flex-col gap-6">
      <SearchSection setGuest={setGuest} />

      {isDayUse && (
        <button
          type="button"
          data-testid="walkin-skip-guest"
          onClick={() => {
            setAnonymous(true);
            setStep(3);
          }}
          className="w-full bg-bg-base border border-dashed border-border-input rounded-xl px-5 py-4 flex items-center justify-center gap-2 text-sm font-semibold text-text-secondary hover:bg-[#F2EBDF] transition-colors"
        >
          <UserCircle size={18} />
          Procedi senza dati ospite (Day Use)
        </button>
      )}

      <div className="flex items-center gap-3 text-xs uppercase tracking-[0.5px] text-text-muted">
        <span className="flex-1 h-px bg-border-base" />
        <span>oppure inserisci manualmente</span>
        <span className="flex-1 h-px bg-border-base" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Field
          label="Nome"
          required
          testId="guest-firstName"
          value={guest.firstName}
          error={errs.firstName}
          onChange={(v) => setGuest({ firstName: v })}
        />
        <Field
          label="Cognome"
          required
          testId="guest-lastName"
          value={guest.lastName}
          error={errs.lastName}
          onChange={(v) => setGuest({ lastName: v })}
        />
        <Field
          label="Telefono"
          required
          testId="guest-phone"
          placeholder="+39 ..."
          value={guest.phone ?? ''}
          error={errs.phone}
          onChange={(v) => setGuest({ phone: v })}
        />
        <Field
          label="Email"
          testId="guest-email"
          type="email"
          value={guest.email ?? ''}
          error={errs.email}
          onChange={(v) => setGuest({ email: v })}
        />
      </div>

      <div className="border-t border-border-base pt-4">
        <button
          type="button"
          onClick={() => setDocOpen((v) => !v)}
          className="flex items-center gap-2 text-sm font-semibold text-text-primary hover:text-accent-primary transition-colors"
          data-testid="guest-document-toggle"
        >
          <ChevronDown size={16} className={cn('transition-transform', docOpen && 'rotate-180')} />
          Documento (opzionale)
        </button>
        {docOpen && (
          <div className="grid grid-cols-2 gap-4 mt-4" data-testid="guest-document-fields">
            <Field
              label="Tipo documento"
              testId="guest-doc-type"
              placeholder="es. CI, PAT, PASS"
              value={guest.documentType ?? ''}
              onChange={(v) => setGuest({ documentType: v })}
            />
            <Field
              label="Numero documento"
              testId="guest-doc-number"
              value={guest.documentNumber ?? ''}
              onChange={(v) => setGuest({ documentNumber: v })}
            />
          </div>
        )}
      </div>

      {/* Compat hidden button per test E2E pre-existenti */}
      <button
        type="button"
        data-testid="walkin-next-2"
        onClick={validateAndAdvance}
        className="hidden"
      >
        Avanti
      </button>
    </section>
  );
}

// --------------------------- SearchSection ---------------------------

function SearchSection({
  setGuest,
}: {
  setGuest: (
    g: Partial<{
      firstName: string;
      lastName: string;
      fullName: string;
      phone: string;
      email: string;
      documentType: string;
      documentNumber: string;
      existingGuestId: string;
    }>,
  ) => void;
}) {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Debounce 250ms.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);

  // Click outside chiude dropdown.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const enabled = debouncedQ.length >= 2;
  const queryRes = useQuery({
    queryKey: ['guests', 'search', debouncedQ],
    queryFn: () => searchGuests(debouncedQ, 8),
    enabled,
    staleTime: 30_000,
  });

  const results = useMemo(() => queryRes.data?.guests ?? [], [queryRes.data]);

  const onSelect = (g: GuestSearchResult) => {
    setGuest({
      existingGuestId: g.id,
      firstName: g.firstName,
      lastName: g.lastName,
      phone: g.phone ?? '',
      email: g.email ?? '',
      documentType: g.documentType ?? '',
      documentNumber: g.documentNumber ?? '',
    });
    setQ('');
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative" data-testid="guest-search-section">
      <label
        className="text-[11px] uppercase tracking-[0.5px] font-semibold text-text-secondary mb-1.5 block"
        htmlFor="guest-search"
      >
        Cerca cliente esistente
      </label>
      <div className="relative">
        <Search
          size={16}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
        />
        <input
          id="guest-search"
          data-testid="guest-search-input"
          type="text"
          value={q}
          placeholder="Nome, email o telefono (min 2 caratteri)"
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="w-full pl-10 pr-3 py-3 rounded-xl border border-border-input bg-bg-card text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-primary focus:ring-2 focus:ring-[#FCF1EA] transition-colors"
        />
      </div>
      {open && enabled && (
        <div
          className="absolute z-10 left-0 right-0 mt-1.5 bg-bg-card border border-border-base rounded-xl shadow-hm-lg max-h-72 overflow-y-auto"
          data-testid="guest-search-dropdown"
        >
          {queryRes.isFetching && (
            <div className="px-4 py-3 text-sm text-text-muted">Caricamento...</div>
          )}
          {!queryRes.isFetching && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-text-muted" data-testid="guest-search-empty">
              Nessun cliente trovato per "{debouncedQ}"
            </div>
          )}
          {!queryRes.isFetching &&
            results.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => onSelect(g)}
                data-testid={`guest-search-item-${g.id}`}
                className="w-full text-left px-4 py-3 hover:bg-bg-base flex flex-col gap-0.5 border-b border-border-base last:border-b-0"
              >
                <span className="text-sm font-semibold text-text-primary">
                  {g.firstName} {g.lastName}
                </span>
                <span className="text-xs text-text-secondary">
                  {[g.phone, g.email].filter(Boolean).join(' · ')}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

// --------------------------- Field ---------------------------

function Field({
  label,
  required,
  testId,
  type,
  value,
  placeholder,
  error,
  onChange,
}: {
  label: string;
  required?: boolean;
  testId: string;
  type?: string;
  value: string;
  placeholder?: string;
  error?: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] uppercase tracking-[0.5px] font-semibold text-text-secondary">
        {label}
        {required && <span className="text-accent-primary ml-0.5">*</span>}
      </span>
      <input
        type={type ?? 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className={cn(
          'w-full px-3.5 py-2.5 rounded-lg border bg-bg-card text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-[#FCF1EA] transition-colors',
          error
            ? 'border-red-500 focus:border-red-500'
            : 'border-border-input focus:border-accent-primary',
        )}
      />
      {error && (
        <small data-testid={`err-${testId.replace('guest-', '')}`} className="text-xs text-red-600">
          {error}
        </small>
      )}
    </label>
  );
}
